/**
 * 수신 그룹 만들기/고치기 — 메일 작성창과 같은 모양으로.
 *
 * 왜 작성창과 같은 모양인가
 *   그룹이 저장하는 건 사람 목록이 아니라 <b>메일 한 통의 수신자 배치</b>다. 그래서 화면도
 *   작성창의 받는이·참조·숨은참조 세 칸을 그대로 쓴다 — <b>어느 칸에 담았는지가 곧 역할</b>이 되므로
 *   따로 역할을 고르는 조작이 없고, 사용자가 새로 배울 것도 없다.
 *
 * 담기는 값은 사본이다
 *   자동완성에서 고른 사람의 이름·이메일을 복사해 저장한다(연락처 id 를 가리키지 않는다).
 *   그래서 원본 연락처를 지워도 그룹은 멀쩡하고, 여기서 주소를 직접 고쳐 이 그룹에서만 다른
 *   주소로 보낼 수도 있다. 대신 연락처 쪽 주소가 바뀌어도 따라오지 않는다 — 의도한 맞바꿈이다.
 */
import React, { useEffect, useState } from "react";
import { Box, Stack, TextField, Typography } from "@mui/material";

import RecipientAutocomplete from "./RecipientAutocomplete";
import { buildMembers, membersToValue } from "./contactGroupAddress";
import { mailApi }  from "../../api/mailApi";
import { notify }   from "../../../../util/toast";
import FormModal    from "../../../../component/common/FormModal";

function SectionHeader({ title, subtitle }) {
    return (
        <Box sx={{ mb: 1.2, pb: 0.6, borderBottom: "1px solid #eef2f6" }}>
            <Typography sx={{ fontSize: 12, fontWeight: 700, color: "#0f172a", letterSpacing: 0.2 }}>
                {title}
            </Typography>
            {subtitle && (
                <Typography sx={{ fontSize: 11, color: "#94a3b8", mt: 0.2 }}>
                    {subtitle}
                </Typography>
            )}
        </Box>
    );
}

/**
 * @param initial null 이면 닫힘, {} 면 신규, { id } 면 수정.
 *   수정일 때 멤버는 여기서 상세를 다시 불러 채운다 — 목록 응답에는 인원 수만 있고 멤버가 없다.
 */
export default function ContactGroupEditDialog({ open, initial, onClose, onSaved }) {
    const [name, setName]               = useState("");
    const [description, setDescription] = useState("");
    const [tos, setTos]                 = useState([]);
    const [ccs, setCcs]                 = useState([]);
    const [bccs, setBccs]               = useState([]);
    const [saving, setSaving]           = useState(false);
    const [loading, setLoading]         = useState(false);

    const isEdit = Boolean(initial?.id);

    useEffect(() => {
        if (!open) return;
        setName(initial?.name || "");
        setDescription(initial?.description || "");
        setTos([]); setCcs([]); setBccs([]);

        if (!initial?.id) return;

        // 수정 — 멤버는 상세로만 온다. 실패해도 이름·설명은 고칠 수 있게 두되,
        // 멤버가 빈 채로 저장되면 통째로 지워지므로 그 위험을 분명히 알린다.
        let alive = true;
        setLoading(true);
        (async () => {
            try {
                const res = await mailApi.getContactGroup(initial.id);
                const data = res?.data ?? res;
                if (!alive) return;
                const members = data?.members || [];
                setTos(membersToValue(members, "TO"));
                setCcs(membersToValue(members, "CC"));
                setBccs(membersToValue(members, "BCC"));
            } catch {
                if (alive) notify("그룹 멤버를 불러오지 못했습니다. 이대로 저장하면 멤버가 모두 지워집니다.", "error");
            } finally {
                if (alive) setLoading(false);
            }
        })();
        return () => { alive = false; };
    }, [open, initial]);

    const handleSave = async () => {
        const trimmed = name.trim();
        if (!trimmed) {
            notify("그룹 이름을 입력해주세요", "warning");
            return;
        }
        const members = buildMembers({ tos, ccs, bccs });
        if (members.length === 0) {
            notify("받는이·참조·숨은참조 중 최소 한 명은 넣어주세요", "warning");
            return;
        }

        setSaving(true);
        try {
            const body = { name: trimmed, description: description.trim(), members };
            if (isEdit) await mailApi.updateContactGroup(initial.id, body);
            else        await mailApi.createContactGroup(body);
            notify(isEdit ? "그룹을 수정했습니다" : "그룹을 만들었습니다", "success");
            onSaved?.();
        } catch (e) {
            notify(e?.response?.data?.message || "저장 실패", "error");
        } finally { setSaving(false); }
    };

    return (
        <FormModal
            open={open}
            onClose={onClose}
            title={isEdit ? "수신 그룹 수정" : "새 수신 그룹"}
            subtitle="담은 칸이 곧 역할입니다 — 작성창에서 이 그룹을 고르면 그대로 채워집니다."
            maxWidth="sm"
            onSave={handleSave}
            saving={saving}
        >
            <Stack spacing={2.5}>
                <Box>
                    <SectionHeader title="그룹" subtitle="이름은 작성창 목록에 그대로 나옵니다." />
                    <Stack spacing={1.5}>
                        <TextField
                            fullWidth required label="그룹 이름" size="small"
                            value={name} onChange={(e) => setName(e.target.value)}
                            InputLabelProps={{ shrink: true }}
                            placeholder="예: A거래처 정기보고"
                            inputProps={{ maxLength: 100 }}
                        />
                        <TextField
                            fullWidth label="설명" size="small"
                            value={description} onChange={(e) => setDescription(e.target.value)}
                            InputLabelProps={{ shrink: true }}
                            placeholder="어떤 자리에서 쓰는 묶음인지 (선택)"
                            inputProps={{ maxLength: 300 }}
                        />
                    </Stack>
                </Box>

                <Box>
                    <SectionHeader
                        title="수신자"
                        subtitle={loading
                            ? "기존 멤버를 불러오는 중입니다…"
                            : "사내 직원·연락처를 검색하거나 외부 주소를 직접 입력하세요."} />
                    {/* 작성창과 같은 컴포넌트·같은 배치. 라벨 순서(받는이 → 참조 → 숨은참조)가
                        중복 처리 우선순위이기도 하다 — contactGroupAddress.buildMembers 주석 참고. */}
                    <RecipientAutocomplete label="받는이"   value={tos}  onChange={setTos} />
                    <RecipientAutocomplete label="참조"     value={ccs}  onChange={setCcs} />
                    <RecipientAutocomplete label="숨은참조" value={bccs} onChange={setBccs} />
                </Box>
            </Stack>
        </FormModal>
    );
}
